import { createContext, useCallback, useEffect, useMemo, useReducer, useRef, useState, type PropsWithChildren } from 'react'
import type { HelperAssignedItem } from '../services/helper/helperAssignedItemsService'
import { useAuth } from './useAuth'
import { useToast } from './useToast'
import { DEFAULT_FOCUS_MODE_STATE, computeExitSummary, elapsedMsFor, formatMinutesSeconds, type FocusModeContextValue, type FocusModeExitSummary, type FocusModeState, type FocusModeTaskFeedSnapshot } from './focusModeCore'
import { recordFocusSession } from '../services/timesheetsService'

type FocusModeInternalState = FocusModeState & {
  pendingEnter: boolean
}

type FocusModeAction =
  | { type: 'ENTER'; items: HelperAssignedItem[]; now: number }
  | { type: 'ENTER_PENDING' }
  | { type: 'EXIT' }
  | { type: 'PAUSE'; now: number }
  | { type: 'RESUME'; now: number }
  | { type: 'ADD_MINUTES'; minutes: number }
  | { type: 'RESTART'; now: number }
  | { type: 'SET_PLANNED_MS'; plannedMs: number }
  | { type: 'NEXT_TASK'; now: number }
  | { type: 'ADD_TO_QUEUE'; items: HelperAssignedItem[] }
  | { type: 'REMOVE_FROM_QUEUE'; itemId: string }

const INITIAL_STATE: FocusModeInternalState = {
  ...DEFAULT_FOCUS_MODE_STATE,
  pendingEnter: false,
}

const focusModeReducer = (
  state: FocusModeInternalState,
  action: FocusModeAction,
): FocusModeInternalState => {
  switch (action.type) {
    case 'ENTER': {
      const [first, ...rest] = action.items
      return {
        ...state,
        active: true,
        workItem: first ?? null,
        queue: rest,
        completedItems: [],
        sessionStartMs: action.now,
        paused: false,
        elapsedBeforePauseMs: 0,
        pendingEnter: false,
      }
    }
    case 'ENTER_PENDING':
      return { ...state, active: false, pendingEnter: true }
    case 'EXIT':
      // The selected duration is a user preference, not session state.
      // Keep it for the next session (and across the storage-backed reload).
      return { ...INITIAL_STATE, timerConfig: state.timerConfig }
    case 'PAUSE':
      if (!state.active || state.paused) return state
      return {
        ...state,
        paused: true,
        elapsedBeforePauseMs: elapsedMsFor(state, action.now),
      }
    case 'RESUME':
      if (!state.active || !state.paused) return state
      return { ...state, paused: false, sessionStartMs: action.now }
    case 'ADD_MINUTES':
      if (!Number.isFinite(action.minutes) || action.minutes === 0) return state
      return {
        ...state,
        timerConfig: {
          plannedMs: Math.max(0, state.timerConfig.plannedMs + action.minutes * 60 * 1000),
        },
      }
    case 'RESTART':
      if (!state.active) return state
      return {
        ...state,
        sessionStartMs: action.now,
        paused: false,
        elapsedBeforePauseMs: 0,
      }
    case 'SET_PLANNED_MS':
      return { ...state, timerConfig: { plannedMs: action.plannedMs } }
    case 'NEXT_TASK': {
      if (!state.active || state.queue.length === 0) return state
      const currentElapsed = elapsedMsFor(state, action.now)
      const nextWorkItem = state.queue[0]
      const remainingQueue = state.queue.slice(1)
      const completedItems = state.workItem
        ? [...state.completedItems, { item: state.workItem, elapsedMs: currentElapsed }]
        : state.completedItems
      return {
        ...state,
        workItem: nextWorkItem,
        queue: remainingQueue,
        completedItems,
        sessionStartMs: action.now,
        paused: false,
        elapsedBeforePauseMs: 0,
      }
    }
    case 'ADD_TO_QUEUE': {
      const existingIds = new Set([
        ...(state.workItem ? [state.workItem.id] : []),
        ...state.queue.map((item) => item.id),
      ])
      const newItems = action.items.filter((item) => !existingIds.has(item.id))
      if (newItems.length === 0) return state
      return {
        ...state,
        queue: [...state.queue, ...newItems],
      }
    }
    case 'REMOVE_FROM_QUEUE':
      return {
        ...state,
        queue: state.queue.filter((item) => item.id !== action.itemId),
      }
  }
}

// eslint-disable-next-line react-refresh/only-export-components -- consumed by useFocusMode.
export const FocusModeContext = createContext<FocusModeContextValue | null>(null)

export const FocusModeProvider = ({ children }: PropsWithChildren) => {
  const { currentUser } = useAuth()
  const { pauseToasts, resumeToasts, showToast, setFocusModeActive } = useToast()
  const [state, dispatch] = useReducer(focusModeReducer, INITIAL_STATE)
  const stateRef = useRef(state)
  const lastSummaryRef = useRef<(FocusModeExitSummary & { workItemTitle?: string }) | null>(null)
  const wasActiveRef = useRef(false)
  const [taskFeed, setTaskFeed] = useState<FocusModeTaskFeedSnapshot>({ items: [], openTodos: () => undefined })

  useEffect(() => {
    stateRef.current = state
  }, [state])

  useEffect(() => {
    if (state.active) {
      pauseToasts()
      setFocusModeActive?.(true)
    } else {
      resumeToasts()
      setFocusModeActive?.(false)
    }
  }, [state.active, pauseToasts, resumeToasts, setFocusModeActive])

  useEffect(() => {
    return () => {
      setFocusModeActive?.(false)
      resumeToasts()
    }
  }, [setFocusModeActive, resumeToasts])

  useEffect(() => {
    if (wasActiveRef.current && !state.active) {
      const summary = lastSummaryRef.current
      if (summary && summary.totalTasksWorked > 0) {
        if (summary.totalTasksWorked > 1) {
          showToast(
            `Focused ${formatMinutesSeconds(summary.totalElapsedMs)} across ${summary.totalTasksWorked} tasks`,
            'success',
          )
        } else if (summary.workItemTitle) {
          showToast(
            `Focused ${formatMinutesSeconds(summary.elapsedMs)} on "${summary.workItemTitle}"`,
            'success',
          )
        }
      }
      lastSummaryRef.current = null
    }
    wasActiveRef.current = state.active
  }, [state.active, showToast])

  useEffect(() => {
    const key = `focus-mode.plannedMinutes.v1:${currentUser?.uid ?? '_anonymous'}`
    try {
      const minutes = Number(window.localStorage.getItem(key))
      if (Number.isFinite(minutes)) dispatch({ type: 'SET_PLANNED_MS', plannedMs: Math.min(240, Math.max(1, minutes)) * 60_000 })
    } catch { /* private storage unavailable */ }
  }, [currentUser])

  const enter = useCallback((workItems?: HelperAssignedItem | HelperAssignedItem[]) => {
    if (typeof window !== 'undefined') window.history.pushState({ focusMode: true }, '', `${window.location.pathname}#focus`)
    if (!workItems || (Array.isArray(workItems) && workItems.length === 0)) {
      dispatch({ type: 'ENTER_PENDING' })
      return
    }
    const items = Array.isArray(workItems) ? workItems : [workItems]
    dispatch({ type: 'ENTER', items, now: Date.now() })
  }, [])

  const exit = useCallback(() => {
    const current = stateRef.current
    const summary = computeExitSummary(current, Date.now())
    lastSummaryRef.current = summary

    if (typeof window !== 'undefined' && window.location.hash === '#focus') window.history.back()

    const FIVE_MINUTES_MS = 5 * 60 * 1000
    for (const completed of current.completedItems) {
      if (completed.elapsedMs >= FIVE_MINUTES_MS && completed.item.workspaceId) {
        const itemWsId = completed.item.workspaceId
        const clientGeneratedId = `focus_${completed.item.id}_${Date.now()}`
        const endedAt = new Date().toISOString()
        const startedAt = new Date(Date.now() - completed.elapsedMs).toISOString()
        void recordFocusSession({
          workspaceId: itemWsId,
          clientGeneratedId,
          startedAt,
          endedAt,
          targetId: completed.item.id,
          targetKind: 'CHECKLIST',
        }).catch((err) => {
          console.warn('Failed to record focus session for completed item:', err)
        })
      }
    }

    dispatch({ type: 'EXIT' })
    return summary
  }, [])

  useEffect(() => {
    const handlePopState = () => {
      if (stateRef.current.active) {
        lastSummaryRef.current = computeExitSummary(stateRef.current, Date.now())
        dispatch({ type: 'EXIT' })
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])


  const pause = useCallback(() => dispatch({ type: 'PAUSE', now: Date.now() }), [])
  const resume = useCallback(() => dispatch({ type: 'RESUME', now: Date.now() }), [])
  const addMinutes = useCallback((minutes: number) => dispatch({ type: 'ADD_MINUTES', minutes }), [])
  const restart = useCallback(() => dispatch({ type: 'RESTART', now: Date.now() }), [])
  const nextTask = useCallback(() => dispatch({ type: 'NEXT_TASK', now: Date.now() }), [])
  const addToQueue = useCallback((items: HelperAssignedItem | HelperAssignedItem[]) => {
    const normalized = Array.isArray(items) ? items : [items]
    dispatch({ type: 'ADD_TO_QUEUE', items: normalized })
  }, [])
  const removeFromQueue = useCallback((itemId: string) => {
    dispatch({ type: 'REMOVE_FROM_QUEUE', itemId })
  }, [])
  const cancelPendingEnter = useCallback(() => {
    if (typeof window !== 'undefined' && window.location.hash === '#focus') window.history.back()
    dispatch({ type: 'EXIT' })
  }, [])
  const setPlannedMinutes = useCallback((minutes: number) => {
    const safeMinutes = Math.min(240, Math.max(1, minutes))
    dispatch({ type: 'SET_PLANNED_MS', plannedMs: safeMinutes * 60_000 })
    try { window.localStorage.setItem(`focus-mode.plannedMinutes.v1:${currentUser?.uid ?? '_anonymous'}`, String(safeMinutes)) } catch { /* private storage unavailable */ }
  }, [currentUser])
  const registerTaskFeed = useCallback(
    (snapshot: FocusModeTaskFeedSnapshot) =>
      setTaskFeed((prev) =>
        prev.items === snapshot.items && prev.openTodos === snapshot.openTodos ? prev : snapshot,
      ),
    [],
  )

  const value = useMemo<FocusModeContextValue>(
    () => ({
      active: state.active,
      workItem: state.workItem,
      queue: state.queue,
      completedItems: state.completedItems,
      sessionStartMs: state.sessionStartMs,
      timerConfig: state.timerConfig,
      paused: state.paused,
      elapsedBeforePauseMs: state.elapsedBeforePauseMs,
      pendingEnter: state.pendingEnter,
      enter,
      exit,
      nextTask,
      addToQueue,
      removeFromQueue,
      pause,
      resume,
      addMinutes,
      restart,
      cancelPendingEnter,
      setPlannedMinutes,
      registerTaskFeed,
      taskFeedItems: taskFeed.items,
      openTodos: taskFeed.openTodos,
    }),
    [state, enter, exit, nextTask, addToQueue, removeFromQueue, pause, resume, addMinutes, restart, cancelPendingEnter, setPlannedMinutes, registerTaskFeed, taskFeed],
  )

  return <FocusModeContext.Provider value={value}>{children}</FocusModeContext.Provider>
}

